/**
 * DATE FIELD USAGE STANDARDS
 *
 * Use this guide for ALL date filtering in the application:
 *
 * LEADS:
 * - created_at: When lead came into system (use for lead counts, CSR performance)
 * - scheduled_date: When appointment is booked (use for scheduling views only)
 * - converted_at: When lead was converted to a job
 *
 * JOBS:
 * - created_at: When job was created from lead
 * - scheduled_date: When job is scheduled to happen (use for scheduling views)
 * - job_date: The business close date — editable by office managers (use for ALL
 *             revenue, financial, and commission reports). Replaces close_date.
 * - closed_at: System audit timestamp — set automatically when job is completed,
 *              NOT used in reports. Do not filter by this field.
 *
 * REVENUE ATTRIBUTION:
 * - Always use jobs.job_date for date filtering in revenue/commission reports.
 * - job_date is a DATE column (e.g. '2026-04-15') — compare with formatDateInputInTz().
 * - If job_date is NULL but status='completed', the job has not been closed yet.
 *
 * CONVERSION METRICS:
 * - Lead created → Lead with job_id set (both use created_at for date filter)
 * - Never mix scheduled_date with created_at in same calculation
 *
 * RATE CALCULATIONS:
 * - Conversion Rate = converted_leads / eligible_leads (total - lost)
 * - Loss Rate = lost_leads / total_leads
 * - Completion Rate = completed_jobs / eligible_jobs (total - cancelled)
 */

export const getRevenueDate = (job: any): Date | null => {
  // Use job_date (editable business date) as the single source of truth
  if (job.job_date) return new Date(job.job_date);
  if (job.status === 'completed' && job.updated_at) return new Date(job.updated_at);
  return null;
};

export const shouldIncludeInRevenue = (job: any, startDate: Date, endDate: Date): boolean => {
  const revenueDate = getRevenueDate(job);
  if (!revenueDate) return false;
  return revenueDate >= startDate && revenueDate <= endDate;
};

/**
 * Calculate conversion rate as percentage of total incoming leads
 * Formula: converted / total * 100
 */
export const calculateConversionRate = (converted: number, total: number): number => {
  return total > 0 ? (converted / total) * 100 : 0;
};

/**
 * Calculate loss rate as percentage of total leads
 */
export const calculateLossRate = (lost: number, total: number): number => {
  return total > 0 ? (lost / total) * 100 : 0;
};

/**
 * Calculate job completion rate as percentage of total jobs
 * Formula: completed / total * 100 (consistent with cancellation rate)
 */
export const calculateCompletionRate = (completed: number, total: number): number => {
  return total > 0 ? (completed / total) * 100 : 0;
};

/**
 * Like calculateConversionRate, but returns null when the denominator is 0 so
 * the UI can render "–" instead of a misleading 0%. Use for cohort metrics
 * whose denominator is a subset that may be empty (Held %, SLA %, Same-call %).
 */
export const calculateRateOrNull = (numerator: number, denominator: number): number | null => {
  return denominator > 0 ? (numerator / denominator) * 100 : null;
};

/** Format a rate from calculateRateOrNull: "12.5%" or "–" when undefined. */
export const formatRate = (value: number | null | undefined): string => {
  return value === null || value === undefined ? '–' : `${value.toFixed(1)}%`;
};
